import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ScannerProfile } from '@rtkwlf/rendall';
import { ScannerConnectionStatus } from '@rtkwlf/rendall';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { useEditScannerDetailsV2 } from '@apis/rendall/queries/use-edit-scanner-config-v2';
import { Stack } from '@packages/fenrir/components/stack';
import { useFeatureFlag } from '@packages/platform/feature-flags';
import { EventKey } from 'ae-constants/constants';

import { ScannerInformation } from '../../../../components/scanner-information/scanner-information';
import { ScannerInformationV2 } from '../../../../components/scanner-information/scanner-information-v2';
import { ScannerConfigFormControls } from '../scanners-config/components/scanner-configuration-tabs/scanner-config-form-controls/scanner-config-form-controls';
import { useScannerConfigurationContext } from '../scanners-config/components/scanner-configuration-tabs/scanner-configuration-context-provider';
import { ScannerConfiguration } from '../scanners-config/components/scanner-configuration-tabs/tabs/scanner-information/scanner-configuration/scanner-configuration';
import { ScannerConfigurationV2 } from '../scanners-config/components/scanner-configuration-tabs/tabs/scanner-information/scanner-configuration/scanner-configuration-v2';
import type { ScannerDetailsForm } from '../scanners-config/components/types';
import { prepareUpdateScannerConfigPayload } from '../scanners-config/prepare-scanner-payload/prepare-scanner-payload';

export const ScannerInformationForm: React.FC<{
  scannerProfile: ScannerProfile;
}> = ({ scannerProfile }) => {
  const { scannerId = '' } = useParams();
  const { setIsFormDirty } = useScannerConfigurationContext();
  const [scannerConfigFF] = useFeatureFlag('SCANNER_CONFIGURE_ACTIVATE');

  const [scannerNameEditMode, setScannerNameEditMode] =
    useState<boolean>(false);

  const { mutate: editScannerConfigV2, isPending: updatePending } =
    useEditScannerDetailsV2(scannerId, scannerProfile.scannerStatus);

  const {
    setValue,
    formState: { isDirty, isValid },
    reset,
    getValues,
    resetField,
    handleSubmit,
  } = useFormContext<ScannerDetailsForm>();

  useEffect(() => setIsFormDirty(isDirty), [isDirty, setIsFormDirty]);

  const onSubmit = useCallback(
    (data: ScannerDetailsForm) => {
      const payload = prepareUpdateScannerConfigPayload(data, scannerProfile);
      editScannerConfigV2(payload, {
        onSuccess: () => {
          reset(data);
        },
      });
    },
    [editScannerConfigV2, reset, scannerProfile],
  );

  const isSubmitDisabled = useMemo(() => {
    if (scannerConfigFF) {
      return !isDirty || !isValid;
    }
    return (
      scannerProfile?.scannerDetails?.connectionStatus !==
        ScannerConnectionStatus.Connected ||
      !isDirty ||
      !isValid ||
      scannerNameEditMode
    );
  }, [
    scannerConfigFF,
    isDirty,
    isValid,
    scannerNameEditMode,
    scannerProfile?.scannerDetails,
  ]);

  const handleEditModeChange = (editMode: boolean) => {
    setScannerNameEditMode(editMode);
  };

  const handleScannerNameUpdate = (scannerName: string) => {
    setValue('name', scannerName, { shouldDirty: true });
  };

  const handleResetNameField = () => resetField('name', { keepDirty: false });

  const preventSubmitOnEnter = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === EventKey.Return) e.preventDefault();
  };

  if (scannerConfigFF) {
    return (
      <Stack gap={3}>
        <ScannerInformationV2 />
        <ScannerConfigurationV2 />
        <ScannerConfigFormControls
          isDirty={isDirty}
          isDisabled={isSubmitDisabled}
          isPending={updatePending}
          handleClick={handleSubmit(onSubmit)}
          reset={reset}
        />
      </Stack>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} onKeyDown={preventSubmitOnEnter}>
      <Stack gap={3}>
        <ScannerInformation
          onEditModeChange={handleEditModeChange}
          onScannerNameUpdate={handleScannerNameUpdate}
          onResetNameField={handleResetNameField}
          scannerNameInForm={getValues('name') || ''}
        />
        <ScannerConfiguration />
        <ScannerConfigFormControls
          isDirty={isDirty}
          isDisabled={isSubmitDisabled}
          isPending={updatePending}
          reset={reset}
        />
      </Stack>
    </form>
  );
};
